/**
 * YouDoScreen - K Addition Within 5 You Do Practice
 *
 * Independent practice where the student solves addition problems
 * without guided help. Updates progress when facts are mastered.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { NUMERAL_FONT_STYLE } from '../../../styles/design-system/typography';
import { KPracticeSettings } from '../types';
import { useKTTS, K_QUESTION_SCREEN_AUDIO } from '../../../hooks/useKAudio';
import { useAuth } from '../../../context/AuthContext';
import { setUserProgress } from '../../../config/api';
import { SetUserProgressRequest } from '../../../types/progress';
import { ADDITION_FACTS, AdditionFactWithId } from './types';
import AnswerBlank from '../../common/AnswerBlank';

interface YouDoScreenProps {
  settings: KPracticeSettings;
  factsToPractice: AdditionFactWithId[];
  onComplete: () => void;
  onBack: () => void;
}

const YouDoScreen: React.FC<YouDoScreenProps> = ({
  settings,
  factsToPractice,
  onComplete,
  onBack
}) => {
  const { user } = useAuth();
  const [currentFactIndex, setCurrentFactIndex] = useState(0);
  const [completedCount, setCompletedCount] = useState(0);
  const [failedQueue, setFailedQueue] = useState<AdditionFactWithId[]>([]);
  const [isRetryPhase, setIsRetryPhase] = useState(false);
  const [currentRetryIndex, setCurrentRetryIndex] = useState(0);
  const [factScores, setFactScores] = useState<Map<string, number>>(new Map());

  const [answerChoices, setAnswerChoices] = useState<number[]>([]);
  const [hasAnswered, setHasAnswered] = useState(false);
  const [ttsHasPlayed, setTtsHasPlayed] = useState(false);
  const [correctAnswerGiven, setCorrectAnswerGiven] = useState(false);
  const [phase, setPhase] = useState<'showing' | 'question'>('showing');

  const { playTTSWithCallback, playChime, isTTSPlaying } = useKTTS();

  // Generate answer choices (target sum + 3 other options from 0-5)
  const generateAnswerChoices = useCallback((target: number) => {
    const choices = [target];
    const used = new Set([target]);

    while (choices.length < 4) {
      const candidate = Math.floor(Math.random() * 6); // 0-5
      if (!used.has(candidate)) {
        choices.push(candidate);
        used.add(candidate);
      }
    }

    return choices.sort(() => Math.random() - 0.5);
  }, []);

  // Get current fact
  const getCurrentFact = useCallback(() => {
    if (isRetryPhase) {
      return failedQueue[currentRetryIndex];
    }
    return factsToPractice[currentFactIndex];
  }, [isRetryPhase, failedQueue, currentRetryIndex, factsToPractice, currentFactIndex]);

  const currentFactWithId = getCurrentFact();
  const currentFact = currentFactWithId ? ADDITION_FACTS[currentFactWithId.factIndex] : undefined;
  const currentSum = currentFact?.sum;

  // Reset state when fact changes
  useEffect(() => {
    if (currentSum !== undefined) {
      setAnswerChoices(generateAnswerChoices(currentSum));
      setHasAnswered(false);
      setTtsHasPlayed(false);
      setCorrectAnswerGiven(false);
      setPhase('showing');
    }
  }, [currentSum, currentFactIndex, currentRetryIndex, generateAnswerChoices]);

  // Start the showing phase
  useEffect(() => {
    if (phase === 'showing' && currentSum !== undefined) {
      setTimeout(() => {
        setPhase('question');
      }, 800);
    }
  }, [phase, currentSum]);

  // Play TTS instruction when entering question phase (plays on every question)
  useEffect(() => {
    if (phase === 'question' && !ttsHasPlayed && settings.speechEnabled && currentSum !== undefined) {
      // Use the generic addition instruction
      const audioFile = K_QUESTION_SCREEN_AUDIO.additionWithin5.instruction;

      setTimeout(() => {
        playTTSWithCallback(audioFile, () => {
          setTtsHasPlayed(true);
        }).catch(() => {
          setTtsHasPlayed(true);
        });
      }, 200);
    } else if (phase === 'question' && !settings.speechEnabled) {
      setTtsHasPlayed(true);
    }
  }, [phase, ttsHasPlayed, settings.speechEnabled, playTTSWithCallback, currentSum]);

  const advanceToNext = useCallback(() => {
    if (isRetryPhase) {
      if (failedQueue.length === 0) {
        onComplete();
      } else if (currentRetryIndex >= failedQueue.length) {
        setCurrentRetryIndex(0);
      } else if (currentRetryIndex < failedQueue.length - 1) {
        setCurrentRetryIndex(currentRetryIndex + 1);
      } else {
        setCurrentRetryIndex(0);
      }
    } else {
      if (currentFactIndex < factsToPractice.length - 1) {
        setCurrentFactIndex(currentFactIndex + 1);
      } else {
        if (failedQueue.length > 0) {
          setIsRetryPhase(true);
          setCurrentRetryIndex(0);
        } else {
          onComplete();
        }
      }
    }

    setHasAnswered(false);
    setCorrectAnswerGiven(false);
    setTtsHasPlayed(false);
    setPhase('showing');
  }, [isRetryPhase, currentRetryIndex, failedQueue.length, currentFactIndex, factsToPractice.length, onComplete]);

  const handleAnswerClick = useCallback((selectedSum: number) => {
    if (isTTSPlaying || hasAnswered || !currentFactWithId) return;

    setHasAnswered(true);
    const isCorrect = selectedSum === currentSum;

    if (isCorrect) {
      setCorrectAnswerGiven(true);

      setFactScores(prev => {
        const newScores = new Map(prev);
        const currentScore = newScores.get(currentFactWithId.factId) || 0;
        const newScore = currentScore + 1;
        newScores.set(currentFactWithId.factId, newScore);

        if (currentScore === 0 && newScore === 1) {
          setCompletedCount(prevCount => prevCount + 1);

          // Update backend fact status to accuracyPractice
          if (user?.userId) {
            setTimeout(async () => {
              try {
                const progressUpdate: SetUserProgressRequest = {
                  facts: {
                    [currentFactWithId.factId]: {
                      status: 'accuracyPractice'
                    }
                  }
                };
                await setUserProgress(user.userId, progressUpdate);
              } catch (error) {
                console.error('Failed to update fact status to accuracyPractice:', error);
              }
            }, 0);
          }
        }

        return newScores;
      });

      if (isRetryPhase) {
        setFailedQueue(prev => {
          const newQueue = prev.filter((_, index) => index !== currentRetryIndex);
          if (newQueue.length === 0) {
            setTimeout(() => onComplete(), 600);
          } else {
            setTimeout(() => {
              setCurrentRetryIndex(prevIndex =>
                prevIndex >= newQueue.length ? 0 : prevIndex
              );
            }, 600);
          }
          return newQueue;
        });
      }

      playChime().then(() => {
        if (!isRetryPhase) {
          setTimeout(() => {
            advanceToNext();
          }, 500);
        }
      });
    } else {
      setFactScores(prev => {
        const newScores = new Map(prev);
        const currentScore = newScores.get(currentFactWithId.factId) || 0;
        const newScore = currentScore - 1;
        newScores.set(currentFactWithId.factId, newScore);

        if (currentScore >= 1 && newScore < 1) {
          setCompletedCount(prevCount => prevCount - 1);
        }

        return newScores;
      });

      if (!correctAnswerGiven) {
        if (!isRetryPhase) {
          setFailedQueue(prev => {
            const alreadyExists = prev.some(f => f.factId === currentFactWithId.factId);
            if (!alreadyExists) {
              return [...prev, currentFactWithId];
            }
            return prev;
          });
        } else {
          const failedFact = failedQueue[currentRetryIndex];
          setFailedQueue(prev => [...prev.filter((_, index) => index !== currentRetryIndex), failedFact]);
        }
      }

      setTimeout(() => {
        setHasAnswered(false);
      }, 1500);
    }
  }, [currentSum, hasAnswered, isTTSPlaying, playChime, correctAnswerGiven, isRetryPhase, currentFactWithId, failedQueue, currentRetryIndex, advanceToNext, onComplete, user]);

  if (!currentFactWithId || currentFact === undefined || currentSum === undefined) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100 flex items-center justify-center">
        <div className="text-2xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100 flex flex-col">
      {/* Header Row */}
      <div className="flex justify-between items-center p-6">
        <button
          onClick={onBack}
          className="bg-gray-400 text-white px-6 py-3 rounded-full font-bold text-lg shadow-lg hover:bg-gray-500 transition-colors"
        >
          ← Back
        </button>

        <div className="bg-orange-400 text-white px-6 py-3 rounded-full font-bold text-lg shadow-lg">
          🌟 You Do Practice
        </div>

        <div className="relative w-16 h-16 opacity-40">
          <svg className="w-16 h-16 transform -rotate-90" viewBox="0 0 32 32">
            <circle cx="16" cy="16" r="14" fill="none" stroke="#d1d5db" strokeWidth="1.5" />
            <circle
              cx="16" cy="16" r="14" fill="none" stroke="#9ca3af" strokeWidth="1.5"
              strokeLinecap="round" strokeDasharray="87.96"
              strokeDashoffset={87.96 * (1 - (completedCount / factsToPractice.length))}
              className="transition-all duration-300 ease-out"
            />
          </svg>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-6 gap-8">
        {/* Addition Equation - Always visible */}
        <motion.div
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.4 }}
          className="text-6xl lg:text-7xl font-bold text-gray-800"
          style={NUMERAL_FONT_STYLE}
        >
          {currentFact.operand1} + {currentFact.operand2} = <AnswerBlank />
        </motion.div>

        {/* Showing Phase */}
        {phase === 'showing' && (
          <div className="text-center">
            <div className="text-2xl lg:text-3xl text-gray-600 animate-pulse mb-2">
              Solve it...
            </div>
          </div>
        )}

        {/* Question Phase - Answer Choices */}
        {phase === 'question' && (
          <div className="w-full max-w-md lg:max-w-lg">
            <div className="grid grid-cols-2 gap-8 lg:gap-12">
              {answerChoices.map((choice, index) => (
                <motion.button
                  key={choice}
                  initial={{ opacity: 0, y: 30, rotate: -5 }}
                  animate={{ opacity: 1, y: 0, rotate: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  whileHover={{ scale: 1.08, rotate: 1 }}
                  whileTap={{ scale: 0.92 }}
                  onClick={() => handleAnswerClick(choice)}
                  disabled={hasAnswered || isTTSPlaying}
                  className={`
                    h-32 lg:h-40 rounded-3xl font-bold text-5xl lg:text-6xl shadow-xl transition-all duration-300
                    ${hasAnswered && choice === currentSum
                      ? 'bg-green-500 text-white border-4 border-green-700 shadow-green-300'
                      : hasAnswered || isTTSPlaying
                      ? 'bg-gray-300 text-gray-500 border-4 border-gray-400 cursor-not-allowed'
                      : 'bg-gradient-to-br from-blue-400 to-blue-600 hover:from-blue-500 hover:to-blue-700 text-white border-4 border-blue-700 cursor-pointer hover:shadow-2xl transform-gpu'
                    }
                  `}
                  style={NUMERAL_FONT_STYLE}
                >
                  {choice}
                </motion.button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Audio Playing Indicator */}
      {isTTSPlaying && (
        <div className="flex justify-center pb-8">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex items-center text-gray-600"
          >
            <div className="animate-pulse mr-2">🔊</div>
            <span>Listen carefully...</span>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default YouDoScreen;
